import { FormattedMessage } from "react-intl";
import { useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import Button from "components/atoms/Button/Button";
import FormField from "components/molecules/FormField/FormField";
import { ReactComponent as DeleteIcon } from "assets/images/icons/Delete.svg";

interface CustomsFieldsProps {
  selectedCommodityIndex: number;
  getInvoiceFormValues: (field: string) => any;
}

function CustomsFields({ selectedCommodityIndex, getInvoiceFormValues }: CustomsFieldsProps) {
  const { control, setValue, getValues } = useFormContext();
  const {
    fields: customsFields,
    append: appendCustoms,
    remove: removeCustoms,
  } = useFieldArray({
    control,
    name: "good_customs_information",
    keyName: "_id",
  });

  const initialGoodCustomsInfo = {
    hs_code: "",
    value: "",
    description: "",
    currency_code:
      getInvoiceFormValues("shipment.total_invoiced_amount_currency") ||
      getInvoiceFormValues("shipment.total_quoted_amount_currency"),
  };

  // Remove or clear the selected customs field
  const onRemoveCustoms = (index: number) => {
    const customField = customsFields[index] as { id?: string };

    // If the field that is being removed is synced by id with the requested customs info, clear the field
    if (customField.id) {
      setValue(`good_customs_information.${index}.description`, "");
      setValue(`good_customs_information.${index}.hs_code`, "");
      setValue(`good_customs_information.${index}.value`, "");
      // If the field is not synced by id, remove the field
    } else removeCustoms(index);
  };

  // Sort the customs fields by id so that the fields are in the same order as the requested customs info
  const sortedCustomsFields = useMemo(
    () =>
      customsFields
        ?.map((item: any, i) => ({ item, originalIndex: i })) // Keep track of the original index
        ?.sort((a, b) => (!!a.item.id === !!b.item.id ? 0 : a.item.id ? -1 : 1)),
    [customsFields],
  );

  return (
    <>
      {sortedCustomsFields.map(({ item, originalIndex }, i) => (
        // eslint-disable-next-line no-underscore-dangle
        <div className="d-flex mt-2" key={item._id}>
          {/* Commodity information */}
          <div className="flex-row w-185-px">
            <FormField
              label={customsFields.length > 0 && i === 0 ? `Commodity information` : ""}
              placeholder="Item description"
              field_id={`good_customs_information.${originalIndex}.description`}
              validations={{
                setValueAs: (v: string) => (!v ? null : v),
                shouldUnregister: true,
              }}
              containerClasses="w-185-px"
            />
          </div>
          {/* HS Code */}
          <div className="ml-2 flex-row w-90-px">
            <FormField
              label={customsFields.length > 0 && i === 0 ? `HS Code` : ""}
              placeholder="HS Code"
              field_id={`good_customs_information.${originalIndex}.hs_code`}
              validations={{
                validate: {
                  onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                },
                setValueAs: (v: string) => (!v ? null : v),
                shouldUnregister: true,
              }}
            />
          </div>
          {/* Value */}
          <div className="ml-2 flex-row w-90-px">
            <FormField
              label={customsFields.length > 0 && i === 0 ? `Value` : ""}
              placeholder="Value"
              field_id={`good_customs_information.${originalIndex}.value`}
              validations={{
                validate: {
                  onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                },
                setValueAs: (v: string) => (!v ? null : v),
                shouldUnregister: true,
              }}
            />
          </div>
          {/* Currency */}
          <div className="ml-2 ">
            <div className="w-90-px ml-3">
              {i === 0 && (
                <p className="font-b3 text-secondary">
                  <FormattedMessage id="general.currency" defaultMessage="Currency" />
                </p>
              )}
              <p className={`font-b4 ${i === 0 ? "mt-3 pt-1" : "mt-2"}`}>
                {selectedCommodityIndex !== -1
                  ? getValues(`good_customs_information.${originalIndex}.currency_code`)
                  : getInvoiceFormValues("shipment.total_invoiced_amount_currency") ||
                    getInvoiceFormValues("shipment.total_quoted_amount_currency")}
              </p>
            </div>
          </div>
          {/* Delete button */}
          <div className="d-flex cursor-pointer mb-2" style={{ marginTop: i === 0 ? 28 : 5 }}>
            <DeleteIcon onClick={() => onRemoveCustoms(originalIndex)} />
          </div>
        </div>
      ))}
      <Button variant="secondary" className="mt-3" onClick={() => appendCustoms(initialGoodCustomsInfo)}>
        <FormattedMessage id="billing.invoiceForm.addCustomsInformation" defaultMessage="Add line of custom information" />
      </Button>
    </>
  );
}

export default CustomsFields;
